import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import type { FormikHelpers } from 'formik';
import { PaperPlaneIcon } from '@rsos/assets/icons';
import { Box, Text, Modal, Button } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { formatPhoneNumber } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/helpers';
import { useCreateConversationOutboundSMSMutation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { cleanPhoneNumber } from '@rsos/utils/phoneNumberUtils';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import type { RootState } from '../../../../../app';
import { initialValues, validationSchema } from './helpers';

type FormValues = {
  phoneNumber: string;
};

type ShareLiveStreamModalProps = {
  isOpened: boolean;
  onClose: () => void;
  shareUrl: string;
};

const ShareLiveStreamModal = ({
  isOpened,
  onClose,
  shareUrl,
}: ShareLiveStreamModalProps) => {
  const orgName = useSelector(
    ({ sinatra }: RootState) =>
      selectUserOrgByApp(sinatra, sinatra.user.currentRole?.application ?? '')
        ?.name,
  );

  const [triggerSendSMS] = useCreateConversationOutboundSMSMutation();

  const handleSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      let callerID = cleanPhoneNumber(values.phoneNumber);

      if (callerID.length === 10) {
        callerID = '1' + callerID;
      }

      try {
        await triggerSendSMS({
          callerID,
          orgName: orgName,
          message: `Join the live video stream: ${shareUrl}`,
        }).unwrap();

        notifySuccess(`Live stream link sent to ${values.phoneNumber}`);
        actions.resetForm();
        onClose();
      } catch (error) {
        notifyError(
          (error as Error)?.message || 'Failed to send SMS. Please try again.',
        );
      } finally {
        actions.setSubmitting(false);
      }
    },
    [shareUrl, onClose, triggerSendSMS, orgName],
  );

  const handlePhoneNumberChange = useCallback(
    (
      value: string,
      setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
    ) => {
      const formatted = formatPhoneNumber(value);
      setFieldValue('phoneNumber', formatted);
    },
    [],
  );

  const handleClose = (resetForm: () => void) => {
    resetForm();
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        setFieldValue,
        errors,
        isSubmitting,
        resetForm,
        touched,
      }) => (
        <Modal
          isOpened={isOpened}
          onClose={() => handleClose(resetForm)}
          width={400}
          id="modal-share-live-stream"
        >
          <Form>
            <ModalHeader onClose={() => handleClose(resetForm)}>
              <Box horizontal align="center" gridColumnGap="8px">
                <PaperPlaneIcon width={20} height={20} color="primaryText" />
                <Text size="xlarge" fontWeight="semibold" color="primaryText">
                  Share via SMS
                </Text>
              </Box>
            </ModalHeader>
            <ModalBody>
              <Box gridRowGap="16px">
                <Text size="normal" color="secondaryText">
                  Send a link to join this live video stream via SMS
                </Text>
                <Box gridRowGap="4px">
                  <Text
                    size="small"
                    color="secondaryText"
                    fontWeight="semibold"
                  >
                    Phone Number
                  </Text>
                  <InputText
                    isDisabled={isSubmitting}
                    error={
                      touched.phoneNumber && errors.phoneNumber
                        ? errors.phoneNumber
                        : undefined
                    }
                    name="phoneNumber"
                    onChange={e =>
                      handlePhoneNumberChange(e.target.value, setFieldValue)
                    }
                    placeholder="(123) 456-7890"
                    setFieldValue={() => setFieldValue('phoneNumber', '')}
                    type="tel"
                    value={values.phoneNumber}
                  />
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Box horizontal justify="flex-end" marginTop="1rem">
                <Button
                  type="button"
                  btnType="tertiary"
                  onClick={() => handleClose(resetForm)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  btnType="primary"
                  isLoadingProp={isSubmitting}
                  disabled={
                    !values.phoneNumber || !!errors.phoneNumber || isSubmitting
                  }
                >
                  Send Link
                </Button>
              </Box>
            </ModalFooter>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default ShareLiveStreamModal;
